/**
 * Нягтлангийн табуудын нийтлэг хэсэг — огнооны шүүлт (URL-д), салбар,
 * нийлүүлэгч, Excel.
 *
 * ⚠️ Огноо нь УБ-ийн өдөр `YYYY-MM-DD`, `to` тэр өдрийг ОРУУЛНА — сервер
 *    (`/accounting/*`) ижил утгаар уншина.
 * ⚠️ Excel-ийг хөтөч дээр, ДЭЛГЭЦЭНД харагдаж буй мөрөөс үүсгэнэ
 *    (`xlsxFile`, @boots/domain) — дэлгэц, файл хоёр зөрөх боломжгүй.
 */

import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ulaanbaatarDateKey, xlsxFile, type XlsxCell } from '@boots/domain';
import { api, saveBlob, type Envelope } from '../../lib/api';
import type { Store } from '../../lib/types';
import { Button, Field, Input, Select } from '../ui';

export interface AccountingFilter {
  from: string;
  to: string;
  storeId: string;
  supplierId: string;
  set: (patch: Partial<Record<'from' | 'to' | 'storeId' | 'supplierId', string>>) => void;
  /** API-ийн query (`from=…&to=…&storeId=…`) */
  query: (extra?: Record<string, string>) => string;
}

const PARAM = { from: 'from', to: 'to', storeId: 'store', supplierId: 'supplier' } as const;

function monthStart(today: string): string {
  return `${today.slice(0, 8)}01`;
}

/** Анхдагч муж — энэ сарын 1-ээс өнөөдөр хүртэл. */
export function useAccountingFilter(): AccountingFilter {
  const [params, setParams] = useSearchParams();
  const today = ulaanbaatarDateKey(new Date());
  const from = params.get(PARAM.from) || monthStart(today);
  const to = params.get(PARAM.to) || today;
  const storeId = params.get(PARAM.storeId) ?? '';
  const supplierId = params.get(PARAM.supplierId) ?? '';

  return {
    from, to, storeId, supplierId,
    set: (patch) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch) as [keyof typeof PARAM, string][]) {
        if (v) next.set(PARAM[k], v);
        else next.delete(PARAM[k]);
      }
      setParams(next, { replace: true });
    },
    query: (extra = {}) =>
      new URLSearchParams({
        from, to,
        ...(storeId ? { storeId } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...extra,
      }).toString(),
  };
}

/** Өмнөх сарын эхний ба сүүлийн өдөр */
function prevMonth(today: string): { from: string; to: string } {
  const [y, m] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const mm = String(pm).padStart(2, '0');
  return { from: `${py}-${mm}-01`, to: `${py}-${mm}-${String(last).padStart(2, '0')}` };
}

export function AccountingFilters({ f, store = true, supplier = false, dates = true }: {
  f: AccountingFilter;
  store?: boolean;
  supplier?: boolean;
  dates?: boolean;
}) {
  const today = ulaanbaatarDateKey(new Date());
  const stores = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
    enabled: store,
    staleTime: 5 * 60_000,
  });
  const suppliers = useQuery({
    queryKey: ['ref-suppliers'],
    queryFn: () => api<Envelope<{ id: string; name: string }[]>>('/ref/suppliers'),
    enabled: supplier,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="flex flex-wrap items-end gap-2.5">
      {dates ? (
        <>
          <Field label="Эхлэх">
            <Input type="date" value={f.from} max={f.to} onChange={(e) => f.set({ from: e.target.value })} className="num" />
          </Field>
          <Field label="Дуусах">
            <Input type="date" value={f.to} min={f.from} onChange={(e) => f.set({ to: e.target.value })} className="num" />
          </Field>
          <div className="flex gap-1.5 pb-0.5">
            <Button variant="ghost" onClick={() => f.set({ from: today, to: today })}>Өнөөдөр</Button>
            <Button variant="ghost" onClick={() => f.set({ from: monthStart(today), to: today })}>Энэ сар</Button>
            <Button variant="ghost" onClick={() => f.set(prevMonth(today))}>Өмнөх сар</Button>
          </div>
        </>
      ) : null}
      {store ? (
        <Field label="Салбар">
          <Select
            value={f.storeId}
            onChange={(v) => f.set({ storeId: v })}
            placeholder="Бүх салбар"
            options={(stores.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          />
        </Field>
      ) : null}
      {supplier ? (
        <Field label="Нийлүүлэгч">
          <Select
            value={f.supplierId}
            onChange={(v) => f.set({ supplierId: v })}
            placeholder="Бүх нийлүүлэгч"
            options={(suppliers.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          />
        </Field>
      ) : null}
    </div>
  );
}

/** Excel-ийн нүд: мөнгө (string) эсвэл текст. `null` = хоосон. */
export type Cell = string | number | { money: string } | null;

/**
 * Нэг хуудастай Excel хадгална: гарчиг, хугацаа, толгой, мөрүүд.
 * Мөнгийг Float болгохгүй — string-ээр шууд бичнэ (CLAUDE.md №3).
 */
export function saveTableXlsx(opts: {
  file: string;
  title: string;
  period?: string;
  headers: string[];
  widths: number[];
  rows: Cell[][];
}): void {
  const toCell = (c: Cell): XlsxCell | null => {
    if (c === null) return null;
    if (typeof c === 'object') return { num: c.money, style: 'money' };
    if (typeof c === 'number') return { num: c };
    return { text: c };
  };
  const bytes = xlsxFile({
    name: opts.title.slice(0, 31),
    widths: opts.widths,
    rows: [
      [{ text: opts.title, style: 'title' }],
      ...(opts.period ? [[{ text: opts.period, style: 'label' } as XlsxCell]] : []),
      [],
      opts.headers.map((h) => ({ text: h, style: 'header' as const })),
      ...opts.rows.map((r) => r.map(toCell)),
    ],
  });
  saveBlob(new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), opts.file);
}

export function ExcelButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <Button variant="ghost" onClick={onClick} disabled={disabled}>
      Excel татах
    </Button>
  );
}
